"use client"

import type React from "react"
import { useState, useEffect, useCallback, useRef } from "react"
import { useNavigate, useSearchParams, Link } from "react-router-dom"
import { useToast } from "../../context/ToastContext"
import ApiService from "../../services/api"
import { useResetPasswordValidation } from "../../hooks/useResetPasswordValidation"
import { ACCOUNT_CONSTANTS } from "../../constants/account-details"
import type { ResetPasswordData } from "../../types/account-details"
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner"
import "./ResetPassword.css"

// Types
interface FormErrors {
  newPassword?: string
  confirmPassword?: string
  general?: string
}

// Constants
const REDIRECT_DELAY = 3000

const ResetPassword: React.FC = () => {
  const { validateResetPassword, getPasswordRequirements, getPasswordStrength } = useResetPasswordValidation()
  const [formData, setFormData] = useState<ResetPasswordData>({
    newPassword: "",
    confirmPassword: "",
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [tokenValid, setTokenValid] = useState<boolean | null>(null) // null = checking, true = valid, false = invalid
  const [showPasswords, setShowPasswords] = useState({
    newPassword: false,
    confirmPassword: false,
  })
  const [searchParams] = useSearchParams()
  const { error, success: showSuccess } = useToast()
  const navigate = useNavigate()
  const hasShownError = useRef(false) // Prevent multiple error toasts

  const token = searchParams.get("token")

  // Validate token on component mount - Fixed useEffect
  useEffect(() => {
    const validateToken = () => {
      if (!token) {
        setTokenValid(false)
        // Only show error once
        if (!hasShownError.current) {
          hasShownError.current = true
          error("Invalid or missing reset token")
        }
        return
      }

      // Token exists, mark as valid
      setTokenValid(true)
    }

    validateToken()
  }, [token]) // Removed error and navigate from dependencies

  // Handle navigation separately to avoid infinite loops
  useEffect(() => {
    if (tokenValid === false) {
      const timer = setTimeout(() => {
        navigate("/login", { replace: true })
      }, 2000) // Give user time to see the error

      return () => clearTimeout(timer)
    }
  }, [tokenValid, navigate])

  // Clear errors when user starts typing
  const clearErrors = useCallback(() => {
    if (Object.keys(errors).length > 0) {
      setErrors({})
    }
  }, [errors])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }))
      clearErrors()
    },
    [clearErrors],
  )

  const togglePasswordVisibility = useCallback((field: keyof typeof showPasswords) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }))
  }, [])

  const validateForm = useCallback((): boolean => {
    const validationError = validateResetPassword(formData)

    if (validationError) {
      // Map the validation error to appropriate field
      if (validationError === ACCOUNT_CONSTANTS.ERROR_MESSAGES.NEW_PASSWORD_REQUIRED) {
        setErrors({ newPassword: validationError })
      } else if (validationError === ACCOUNT_CONSTANTS.ERROR_MESSAGES.CONFIRM_PASSWORD_REQUIRED) {
        setErrors({ confirmPassword: validationError })
      } else if (validationError === ACCOUNT_CONSTANTS.ERROR_MESSAGES.PASSWORDS_DONT_MATCH) {
        setErrors({ confirmPassword: validationError })
      } else if (
        validationError === ACCOUNT_CONSTANTS.ERROR_MESSAGES.PASSWORD_TOO_SHORT ||
        validationError === ACCOUNT_CONSTANTS.ERROR_MESSAGES.INVALID_PASSWORD_FORMAT
      ) {
        setErrors({ newPassword: validationError })
      } else {
        setErrors({ general: validationError })
      }
      return false
    }

    return true
  }, [formData, validateResetPassword])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()

      if (!validateForm()) {
        return
      }

      if (!token) {
        error("Invalid reset token")
        return
      }

      setLoading(true)
      setErrors({})

      try {
        const payload = {
          new_password: formData.newPassword,
          confirm_password: formData.confirmPassword,
        }

        const response = await ApiService.resetPassword(token, payload.new_password, payload.confirm_password)

        if (response.status === 200) {
          // setSuccess(true)
          showSuccess(response.message || "Password reset successful! Redirecting to login...")

          // Redirect after delay
          setTimeout(() => {
            navigate("/login", { replace: true })
          }, REDIRECT_DELAY)
        } else {
          setErrors({ general: response.message })
          error(response.message)
        }
      } catch (err: any) {
        const errorMessage = err?.response?.data?.message || err?.message || "Failed to reset password"
        setErrors({ general: errorMessage })
        error(errorMessage)
      } finally {
        setLoading(false)
      }
    },
    [formData, token, validateForm, error, showSuccess, navigate],
  )

  // Show loading while checking token
  if (tokenValid === null) {
    return (
      <div className="reset-password-container">
        <div className="token-validation-loading">
          <div className="loading-content">
            <LoadingSpinner size="large" />
            <p>Validating reset token...</p>
          </div>
        </div>
      </div>
    )
  }

  // Show error state if token is invalid
  if (tokenValid === false) {
    return (
      <div className="reset-password-container">
        <div className="login-header">
          <Link to="/" className="login-logo-link">
            <div className="logo">
              <div className="logo-icon">
                <div className="reset-logo-icon">⚡</div>
              </div>
              <span className="reset-logo-text">Netzsync</span>
            </div>
          </Link>
        </div>
        <div className="token-error-container">
          <div className="error-content">
            {/* <div className="error-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div> */}
            <h2>Invalid Reset Token</h2>
            <p>The password reset link is invalid or has expired. Please request a new password reset.</p>
            <div className="error-actions">
              <Link to="/forgot-password" className="btn btn-primary">
                Request New Reset
              </Link>
              <Link to="/login" className="btn btn-secondary">
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Success state component
  // if (success) {
  //   return (
  //     <div className="reset-password-page">
  //       <div className="reset-password-background">
  //         <img
  //           src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/image-0LIZI6pbLCVmhBTwbKZwjnXZXuxdU8.png"
  //           alt="Background"
  //           className="background-image"
  //         />
  //       </div>
  //       <div className="reset-password-modal">
  //         <div className="success-content">
  //           <div className="success-icon">
  //             <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
  //               <polyline points="20,6 9,17 4,12" />
  //             </svg>
  //           </div>
  //           <h2>Password Reset Successful!</h2>
  //           <p>Your password has been updated successfully. You will be redirected to the login page shortly.</p>
  //           <div className="loading-animation">
  //             <LoadingSpinner size="large" />
  //           </div>
  //           <div className="redirect-info">
  //             <p>Redirecting in a few seconds...</p>
  //             <Link to="/login" className="manual-redirect">
  //               Go to Login Now
  //             </Link>
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   )
  // }

  const passwordRequirements = getPasswordRequirements(formData.newPassword)
  const passwordStrength = getPasswordStrength(formData.newPassword)

  return (
    <div className="reset-password-container">
      {/* Header with Logo */}
      <div className="login-header">
        <Link to="/" className="login-logo-link">
          <div className="logo">
            <div className="logo-icon">
              <div className="reset-logo-icon">⚡</div>
            </div>
            <span className="reset-logo-text">Netzsync</span>
          </div>
        </Link>
      </div>

      <div className="reset-password-content">
        {/* Left side - Illustration */}
        <div className="reset-password-left">
          <div className="illustration-container">
            <div className="sync-animation">
              <div className="sync-icon">
                <svg width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c2.35 0 4.48.9 6.07 2.38" />
                  <path d="M17 4l4 4-4 4" />
                </svg>
              </div>
            </div>
            <div className="illustration-text">
              <h2>Secure Password Reset</h2>
              <p>Create a strong new password to keep your account secure and protected.</p>
            </div>
          </div>
        </div>

        {/* Right side - Form */}
        <div className="reset-password-right">
          <div className="form-container">
            <div className="form-header">
              <h1>Reset Password</h1>
              <p>Update your password to keep your account secure. Make sure to use a strong password.</p>
            </div>

            <form onSubmit={handleSubmit} className="reset-password-form" noValidate>
              {/* General Error */}
              {errors.general && (
                <div className="error-message general-error">
                  {/* <span className="error-icon">⚠️</span> */}
                  {errors.general}
                </div>
              )}

              {/* Password Fields Row */}
              <div className="password-fields-row">
                {/* New Password Field */}
                <div className="form-group">
                  <label htmlFor="newPassword" className="form-label">
                    New Password <span className="required">*</span>
                  </label>
                  <div className="password-input-wrapper">
                    <input
                      id="newPassword"
                      type={showPasswords.newPassword ? "text" : "password"}
                      name="newPassword"
                      placeholder="Enter your new password"
                      value={formData.newPassword}
                      onChange={handleChange}
                      className={`form-input ${errors.newPassword ? "error" : ""}`}
                      required
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => togglePasswordVisibility("newPassword")}
                      aria-label={showPasswords.newPassword ? "Hide password" : "Show password"}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        {showPasswords.newPassword ? (
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        ) : (
                          <>
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </>
                        )}
                        {showPasswords.newPassword && <line x1="1" y1="1" x2="23" y2="23" />}
                      </svg>
                    </button>
                  </div>
                  {errors.newPassword && <div className="field-error">{errors.newPassword}</div>}
                </div>

                {/* Confirm Password Field */}
                <div className="form-group">
                  <label htmlFor="confirmPassword" className="form-label">
                    Confirm New Password <span className="required">*</span>
                  </label>
                  <div className="password-input-wrapper">
                    <input
                      id="confirmPassword"
                      type={showPasswords.confirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      placeholder="Confirm your new password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={`form-input ${errors.confirmPassword ? "error" : ""}`}
                      required
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => togglePasswordVisibility("confirmPassword")}
                      aria-label={showPasswords.confirmPassword ? "Hide password" : "Show password"}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        {showPasswords.confirmPassword ? (
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        ) : (
                          <>
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </>
                        )}
                        {showPasswords.confirmPassword && <line x1="1" y1="1" x2="23" y2="23" />}
                      </svg>
                    </button>
                  </div>
                  {errors.confirmPassword && <div className="field-error">{errors.confirmPassword}</div>}
                </div>
              </div>

              {/* Password Length Hint */}
              {/* <div className="password-hint">
                Must be at least {ACCOUNT_CONSTANTS.VALIDATION.MIN_PASSWORD_LENGTH} characters long
              </div> */}

              {/* Password Requirements */}
              <div className="password-requirements">
                <h4>Password Requirements:</h4>
                <div className="requirements-list">
                  <div className={`requirement-item ${passwordRequirements.minLength ? "valid" : ""}`}>
                    <div className="requirement-checkbox">
                      {passwordRequirements.minLength ? (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="20,6 9,17 4,12" />
                        </svg>
                      ) : (
                        <div className="checkbox-empty"></div>
                      )}
                    </div>
                    <span>At least {ACCOUNT_CONSTANTS.VALIDATION.MIN_PASSWORD_LENGTH} characters long</span>
                  </div>

                  <div className={`requirement-item ${passwordRequirements.hasUppercase ? "valid" : ""}`}>
                    <div className="requirement-checkbox">
                      {passwordRequirements.hasUppercase ? (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="20,6 9,17 4,12" />
                        </svg>
                      ) : (
                        <div className="checkbox-empty"></div>
                      )}
                    </div>
                    <span>Contains uppercase letter</span>
                  </div>

                  <div className={`requirement-item ${passwordRequirements.hasLowercase ? "valid" : ""}`}>
                    <div className="requirement-checkbox">
                      {passwordRequirements.hasLowercase ? (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="20,6 9,17 4,12" />
                        </svg>
                      ) : (
                        <div className="checkbox-empty"></div>
                      )}
                    </div>
                    <span>Contains lowercase letter</span>
                  </div>

                  <div className={`requirement-item ${passwordRequirements.hasNumber ? "valid" : ""}`}>
                    <div className="requirement-checkbox">
                      {passwordRequirements.hasNumber ? (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="20,6 9,17 4,12" />
                        </svg>
                      ) : (
                        <div className="checkbox-empty"></div>
                      )}
                    </div>
                    <span>Contains number</span>
                  </div>

                  <div className={`requirement-item ${passwordRequirements.hasSpecialChar ? "valid" : ""}`}>
                    <div className="requirement-checkbox">
                      {passwordRequirements.hasSpecialChar ? (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="20,6 9,17 4,12" />
                        </svg>
                      ) : (
                        <div className="checkbox-empty"></div>
                      )}
                    </div>
                    <span>Contains special character</span>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <button type="submit" className="reset-button" disabled={loading}>
                {loading ? (
                  <>
                    <LoadingSpinner size="small" />
                    Submitting..
                  </>
                ) : (
                  "Submit"
                )}
              </button>

              {/* Security Tips */}
              <div className="security-tips">
                <h4>Security Tips:</h4>
                <div className="tips-content">
                  <div className="tip-item">
                    <div className="tip-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      </svg>
                    </div>
                    <span>Use a unique password that you don't use for other accounts</span>
                  </div>
                  <div className="tip-item">
                    <div className="tip-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 12l2 2 4-4" />
                        <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3" />
                        <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3" />
                      </svg>
                    </div>
                    <span>Consider using a password manager to generate and store strong passwords</span>
                  </div>
                </div>
              </div>

              {/* Form Footer */}
              <div className="form-footer">
                <p>
                  Remember your password?{" "}
                  <Link to="/login" className="login-link">
                    Back to Login
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ResetPassword
